import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get } from '../../lib/apiClient';
import { cn } from '@vd/ui';
import type { CaptureMode } from '@vd/types';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import { PeriodPicker, defaultPeriod, type PeriodValue } from './PeriodPicker';
import { CostsView } from './CostsView';
import { planClass } from './planBadge';
import { ADMIN_USAGE_SCREEN, EVERY_SCREEN } from '../../lib/screenNames';
import styles from './Admin.module.css';

/**
 * Admin → Usage.
 *
 * Inner tabs: By account · By user · Costs · Allowances. The shared
 * PeriodPicker lives top-right of the page header (inline with the
 * H1) and applies to the first three. Default range is "Last 30 days".
 * Allowances is this calendar month only (TOADO-655), so it ignores it.
 *
 * Time range sent to the API as a `from=YYYY-MM-DD&to=YYYY-MM-DD`
 * pair; the backend's resolveUsageRange handles inclusive bounds +
 * inverted-range swap.
 */
interface UsageCompany {
  id: string;
  name: string;
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  memberCount: number;
  seatsPurchased: number;
  ticketsInPeriod: number;
  widgetSubmissionsInPeriod: number;
  extensionCaptureModesInPeriod?: Partial<CaptureModeCounts> & Record<string, unknown>;
  activeWidgets: number;
  ticketQuotaOverride: number | null;
  effectiveCap: number | null;
  capUtilization: number | null;
}

type CaptureModeBucket = CaptureMode | 'unknown';
export type CaptureModeCounts = Record<CaptureModeBucket, number>;

const MODE_PRESENTATION: Array<{
  key: CaptureModeBucket;
  label: string;
  color: string;
}> = [
  { key: 'pin', label: 'Pin', color: '#f97316' },
  { key: 'classic', label: 'Classic', color: '#3b82f6' },
  { key: 'edit', label: 'Edit', color: '#8b5cf6' },
  { key: 'markup', label: 'Markup', color: '#ec4899' },
  { key: 'widget', label: 'Widget', color: '#10b981' },
  { key: 'unknown', label: 'Historical unknown', color: '#94a3b8' },
];
const MODE_BUCKETS = new Set<string>(MODE_PRESENTATION.map(({ key }) => key));

interface UsageUser {
  id: string;
  email: string;
  name: string;
  ticketsInPeriod: number;
}

interface UsageCompaniesResponse {
  period: string;
  from: string;
  to: string;
  companies: UsageCompany[];
}

interface UsageUsersResponse {
  period: string;
  from: string;
  to: string;
  users: UsageUser[];
}

/** One meter this month: allowance null is unlimited, and so is share. */
export interface AllowanceMeter {
  used: number;
  allowance: number | null;
  share: number | null;
}

interface AllowanceCompany {
  id: string;
  name: string;
  plan: string;
  /** The tier that counts now, Business's included Starter among them. */
  widgetTier: string | null;
  meters: {
    recordingMinutes: AllowanceMeter;
    narrationMinutes: AllowanceMeter;
    visitorMinutes: AllowanceMeter;
    widgetReports: AllowanceMeter;
    storageBytes: AllowanceMeter;
    /** Against the ticket cap (an admin's quota, or Free's), which stops widget reports first; unlimited without one. */
    tickets: AllowanceMeter;
  };
  addons: {
    recordingBoostMinutes: number;
    narrationBoostMinutes: number;
    recordingPackSeconds: number;
    narrationPackSeconds: number;
    videoHistoryActive: boolean;
    videoHistoryThrough: string | null;
  };
  /** The largest share of any meter. */
  fullest: number;
}

interface AllowancesResponse {
  month: string;
  /** How many workspaces had something to show; the fullest are returned. */
  total: number;
  companies: AllowanceCompany[];
}

type Tab = 'accounts' | 'users' | 'costs' | 'allowances';
type PlanFilter = 'all' | 'free' | 'personal' | 'team' | 'business' | 'enterprise';

export function UsagePage() {
  const [tab, setTab] = useState<Tab>('accounts');
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod);
  const navigate = useNavigate();

  return (
    <>
      {/* The header, the line under it and the tabs are on every tab (TOADO-701). */}
      <div className={styles.pageHeader} data-toado-view={EVERY_SCREEN}>
        <h1 className={styles.h1}>Usage</h1>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      <p className={styles.sub} data-toado-view={EVERY_SCREEN}>
        Tickets created per account and per user in the selected window.
      </p>

      <div className={styles.innerTabs} data-toado-view={EVERY_SCREEN}>
        <button
          type="button"
          className={cn(styles.innerTab, tab === 'accounts' && styles.innerTabActive)}
          onClick={() => setTab('accounts')}
        >
          By account
        </button>
        <button
          type="button"
          className={cn(styles.innerTab, tab === 'users' && styles.innerTabActive)}
          onClick={() => setTab('users')}
        >
          By user
        </button>
        <button
          type="button"
          className={cn(styles.innerTab, tab === 'costs' && styles.innerTabActive)}
          onClick={() => setTab('costs')}
        >
          Costs
        </button>
        <button
          type="button"
          className={cn(styles.innerTab, tab === 'allowances' && styles.innerTabActive)}
          onClick={() => setTab('allowances')}
        >
          Allowances (this month)
        </button>
      </div>

      {/* Each tab is a screen for pins (TOADO-701); the header and the tabs
          above are the same on all of them. */}
      <div data-toado-view={`${ADMIN_USAGE_SCREEN}/${tab}`}>
        {tab === 'accounts' && <UsageByAccount period={period} onAccountClick={(id) => navigate(`/admin/accounts?focus=${encodeURIComponent(id)}`)} />}
        {tab === 'users' && <UsageByUser period={period} onUserClick={(id) => navigate(`/admin/users?focus=${encodeURIComponent(id)}`)} />}
        {tab === 'costs' && <CostsView period={period} />}
        {tab === 'allowances' && <UsageAllowances onAccountClick={(id) => navigate(`/admin/accounts?focus=${encodeURIComponent(id)}`)} />}
      </div>
    </>
  );
}

function rangeQuery(period: PeriodValue): string {
  return `from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`;
}

function UsageByAccount({ period, onAccountClick }: { period: PeriodValue; onAccountClick: (id: string) => void }) {
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const q = rangeQuery(period);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'usage', 'companies', q],
    queryFn: () => get<UsageCompaniesResponse>(`/admin/usage/companies?${q}`),
    staleTime: 30_000,
  });
  const filtered = (data?.companies ?? []).filter((c) => planFilter === 'all' || c.plan === planFilter);
  const captureModeTotals = aggregateCaptureModeCounts(filtered);
  const captureModeTelemetryAvailable = filtered.length === 0
    || filtered.every((company) => company.extensionCaptureModesInPeriod !== undefined);

  const columns: AdminTableColumn<UsageCompany>[] = [
    {
      key: 'name', label: 'Account', sortable: true,
      sortAccessor: (r) => r.name.toLowerCase(),
      render: (c) => (
        <div className={styles.userCell}>
          <button type="button" className={cn(styles.userName, styles.linkChip)} onClick={() => onAccountClick(c.id)}>
            {c.name}
          </button>
          <span className={styles.userEmail}>{c.id}</span>
        </div>
      ),
    },
    {
      key: 'plan', label: 'Plan', sortable: true, sortAccessor: (r) => r.plan,
      render: (c) => <span className={cn(styles.planBadge, planClass(c.plan))}>{c.plan}</span>,
    },
    { key: 'members', label: 'Members', center: true, sortable: true, sortAccessor: (r) => r.memberCount, render: (c) => c.memberCount },
    {
      key: 'tickets', label: 'Tickets', center: true, sortable: true, sortAccessor: (r) => r.ticketsInPeriod,
      render: (c) => <strong>{c.ticketsInPeriod}</strong>,
    },
    {
      // Widget submissions in the window + how many live widgets the account
      // has. The submission count is a subset of Tickets, so it reads as
      // "of those, N via widget"; the small "· K live" note is the
      // period-independent active-widget count.
      key: 'widget', label: 'Widget', center: true, sortable: true, sortAccessor: (r) => r.widgetSubmissionsInPeriod,
      render: (c) => (
        c.widgetSubmissionsInPeriod === 0 && c.activeWidgets === 0
          ? <span className={styles.muted}>-</span>
          : (
            <span>
              <strong>{c.widgetSubmissionsInPeriod}</strong>
              {c.activeWidgets > 0 && (
                <span className={styles.muted} style={{ marginLeft: 4 }}>· {c.activeWidgets} live</span>
              )}
            </span>
          )
      ),
    },
    {
      key: 'cap', label: 'Cap', sortable: true,
      sortAccessor: (r) => r.effectiveCap ?? Number.POSITIVE_INFINITY,
      render: (c) => (
        <span className={styles.muted}>
          {c.effectiveCap == null ? '-' : c.effectiveCap}
          {c.ticketQuotaOverride != null && <span className={styles.overrideBadge}>override</span>}
        </span>
      ),
    },
    {
      key: 'util', label: 'Utilization', sortable: true,
      sortAccessor: (r) => r.capUtilization ?? -1,
      render: (c) => c.capUtilization == null
        ? <span className={styles.muted}>-</span>
        : <UtilizationBar ratio={c.capUtilization} />,
    },
  ];

  if (error) return <p style={{ color: 'var(--danger)' }}>Failed to load: {String(error)}</p>;
  if (isLoading || !data) return <p className={styles.muted}>Loading…</p>;

  return (
    <>
      <CaptureModeMix
        counts={captureModeTotals}
        filteredPlan={planFilter}
        telemetryAvailable={captureModeTelemetryAvailable}
      />
      <AdminTable<UsageCompany>
      columns={columns}
      rows={filtered}
      getRowId={(c) => c.id}
      searchTextFor={(c) => `${c.name} ${c.id} ${c.plan}`}
      searchPlaceholder="Search accounts…"
      initialSortKey="tickets"
      initialSortDir="desc"
      emptyText="No accounts match."
      filterBar={() => (
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Plan
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as PlanFilter)}
            style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'inherit' }}
          >
            <option value="all">All</option>
            <option value="free">Free</option>
            <option value="personal">Personal</option>
            <option value="team">Team</option>
            <option value="business">Business</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </label>
      )}
      />
    </>
  );
}

export function CaptureModeMix({
  counts,
  filteredPlan,
  telemetryAvailable,
}: {
  counts: CaptureModeCounts;
  filteredPlan: PlanFilter;
  telemetryAvailable: boolean;
}) {
  const total = MODE_PRESENTATION.reduce((sum, { key }) => sum + counts[key], 0);
  const pinShare = captureModePinShare(counts);
  const visibleModes = MODE_PRESENTATION.filter(({ key }) => counts[key] > 0);
  const knownModes = visibleModes.filter(({ key }) => key !== 'unknown');
  const knownTotal = knownModes.reduce((sum, { key }) => sum + counts[key], 0);
  const ariaLabel = knownModes.map(({ key, label }) => `${label} ${counts[key]}`).join(', ');

  return (
    <section className={styles.modeMixCard} aria-labelledby="capture-mode-mix-title">
      <div className={styles.modeMixHeader}>
        <div>
          <h2 id="capture-mode-mix-title" className={styles.modeMixTitle}>Extension capture modes</h2>
          <p className={styles.modeMixSubtitle}>
            {filteredPlan === 'all' ? 'All plans' : `${filteredPlan} plans`} in the selected period
          </p>
        </div>
        <div className={styles.modeMixDecision}>
          <strong>
            {!telemetryAvailable
              ? 'Telemetry unavailable'
              : pinShare == null ? 'No known mode data' : `${pinShare}% pin`}
          </strong>
          <span>{telemetryAvailable ? '40% checkpoint' : 'API rollout in progress'}</span>
        </div>
      </div>

      {!telemetryAvailable ? (
        <p className={styles.modeMixEmpty} role="status">
          Capture-mode telemetry will appear when the API rollout reaches this environment.
        </p>
      ) : knownTotal === 0 ? (
        <p className={styles.modeMixEmpty}>No extension captures with a known mode</p>
      ) : (
        <div className={styles.modeMixBar} role="img" aria-label={ariaLabel}>
          {knownModes.map(({ key, color }) => (
          <span
            key={key}
            className={styles.modeMixSegment}
            style={{ width: `${(counts[key] / knownTotal) * 100}%`, background: color }}
          />
          ))}
          <span className={styles.modeMixCheckpoint} aria-hidden="true" title="40% checkpoint" />
        </div>
      )}

      {telemetryAvailable && total > 0 && <div className={styles.modeMixLegend}>
        {visibleModes.map(({ key, label, color }) => (
          <span key={key} className={styles.modeMixLegendItem}>
            <span className={styles.modeMixSwatch} style={{ background: color }} />
            {label} <strong>{counts[key]}</strong>
          </span>
        ))}
      </div>}
      {telemetryAvailable && counts.unknown > 0 && (
        <p className={styles.modeMixFootnote}>
          Historical unknown stays separate. It is not counted as classic or in the pin checkpoint.
        </p>
      )}
    </section>
  );
}

/** Pin adoption among known extension modes, excluding uninferable history. */
export function captureModePinShare(counts: CaptureModeCounts): number | null {
  const knownTotal = counts.pin + counts.classic + counts.edit + counts.markup + counts.widget;
  return knownTotal > 0 ? Math.floor((counts.pin / knownTotal) * 1_000) / 10 : null;
}

/**
 * Sum rolling API responses without allowing a future mode to disappear.
 * An older web build cannot label that mode yet, so it joins unknown until
 * the frontend updates and can include it honestly in the known-mode gate.
 */
export function aggregateCaptureModeCounts(
  companies: Array<{ extensionCaptureModesInPeriod?: Record<string, unknown> }>,
): CaptureModeCounts {
  const totals: CaptureModeCounts = { pin: 0, edit: 0, markup: 0, classic: 0, widget: 0, unknown: 0 };
  for (const company of companies) {
    for (const [key, rawValue] of Object.entries(company.extensionCaptureModesInPeriod ?? {})) {
      const value = Number(rawValue);
      if (!Number.isFinite(value) || value <= 0) continue;
      const bucket = MODE_BUCKETS.has(key) ? key as CaptureModeBucket : 'unknown';
      totals[bucket] += value;
    }
  }
  return totals;
}

function UsageByUser({ period, onUserClick }: { period: PeriodValue; onUserClick: (id: string) => void }) {
  const q = rangeQuery(period);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'usage', 'users', q],
    queryFn: () => get<UsageUsersResponse>(`/admin/usage/users?${q}`),
    staleTime: 30_000,
  });

  const columns: AdminTableColumn<UsageUser>[] = [
    {
      key: 'user', label: 'User', sortable: true,
      sortAccessor: (r) => r.name.toLowerCase(),
      render: (u) => (
        <div className={styles.userCell}>
          <button type="button" className={cn(styles.userName, styles.linkChip)} onClick={() => onUserClick(u.id)}>
            {u.name}
          </button>
          <span className={styles.userEmail}>{u.email}</span>
        </div>
      ),
    },
    {
      key: 'tickets', label: 'Tickets', center: true, sortable: true, sortAccessor: (r) => r.ticketsInPeriod,
      render: (u) => <strong>{u.ticketsInPeriod}</strong>,
    },
  ];

  if (error) return <p style={{ color: 'var(--danger)' }}>Failed to load: {String(error)}</p>;
  if (isLoading || !data) return <p className={styles.muted}>Loading…</p>;

  return (
    <AdminTable<UsageUser>
      columns={columns}
      rows={data.users}
      getRowId={(u) => u.id}
      searchTextFor={(u) => `${u.name} ${u.email}`}
      searchPlaceholder="Search users…"
      initialSortKey="tickets"
      initialSortDir="desc"
      emptyText="No tickets created in this range."
    />
  );
}

const GB = 1024 ** 3;

type AllowanceMeterKey = keyof AllowanceCompany['meters'];

/**
 * This month's usage against each workspace's allowance (TOADO-655), read
 * through the functions the server enforces with, so a full meter here is one
 * the workspace is being held to. The API sends the fullest first.
 */
function UsageAllowances({ onAccountClick }: { onAccountClick: (id: string) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'usage', 'allowances'],
    queryFn: () => get<AllowancesResponse>('/admin/usage/allowances'),
    staleTime: 30_000,
  });

  const meterColumn = (key: AllowanceMeterKey, label: string): AdminTableColumn<AllowanceCompany> => ({
    key, label, sortable: true,
    // Unlimited sorts below every limited meter, empty ones included.
    sortAccessor: (r) => r.meters[key].share ?? -1,
    render: (c) => <MeterCell meter={c.meters[key]} storage={key === 'storageBytes'} note={addonNote(c, key)} />,
  });

  const columns: AdminTableColumn<AllowanceCompany>[] = [
    {
      key: 'name', label: 'Account', sortable: true,
      sortAccessor: (r) => r.name.toLowerCase(),
      render: (c) => (
        <div className={styles.userCell}>
          <button type="button" className={cn(styles.userName, styles.linkChip)} onClick={() => onAccountClick(c.id)}>
            {c.name}
          </button>
          <span className={styles.userEmail}>{c.id}</span>
        </div>
      ),
    },
    {
      key: 'plan', label: 'Plan', sortable: true, sortAccessor: (r) => r.plan,
      render: (c) => <span className={cn(styles.planBadge, planClass(c.plan))}>{c.plan}</span>,
    },
    {
      key: 'tier', label: 'Widget tier', sortable: true, sortAccessor: (r) => r.widgetTier,
      render: (c) => c.widgetTier
        ? <span style={{ textTransform: 'capitalize' }}>{c.widgetTier}</span>
        : <span className={styles.muted}>-</span>,
    },
    meterColumn('recordingMinutes', 'Recording min'),
    meterColumn('narrationMinutes', 'Narration min'),
    meterColumn('visitorMinutes', 'Visitor min'),
    meterColumn('widgetReports', 'Widget reports'),
    meterColumn('storageBytes', 'Storage'),
    meterColumn('tickets', 'Tickets (cap)'),
    {
      key: 'fullest', label: 'Fullest', numeric: true, sortable: true,
      title: 'The largest share of any meter',
      sortAccessor: (r) => r.fullest,
      render: (c) => <strong>{Math.round(c.fullest * 100)}%</strong>,
    },
  ];

  if (error) return <p style={{ color: 'var(--danger)' }}>Failed to load: {String(error)}</p>;
  if (isLoading || !data) return <p className={styles.muted}>Loading…</p>;

  return (
    <>
      <p className={styles.muted} style={{ fontSize: 12, margin: '0 0 8px' }}>
        This month ({monthName(data.month)}) only, whatever the period above says. Each allowance is what the
        server holds the workspace to, its add-ons and widget tier included (* marks add-ons). Amber from 80%,
        red at 100%.
        {data.total > data.companies.length && ` The ${data.companies.length.toLocaleString()} fullest of ${data.total.toLocaleString()} are shown.`}
      </p>
      <AdminTable<AllowanceCompany>
        columns={columns}
        rows={data.companies}
        getRowId={(c) => c.id}
        searchTextFor={(c) => `${c.name} ${c.id} ${c.plan} ${c.widgetTier ?? ''}`}
        searchPlaceholder="Search accounts…"
        initialSortKey="fullest"
        initialSortDir="desc"
        emptyText="No workspace has recorded, taken a widget report or held an add-on this month."
      />
    </>
  );
}

/** "850 of 1,000" or "12, unlimited", with a bar for a limited meter. Storage is in GB. */
function MeterCell({ meter, storage = false, note }: { meter: AllowanceMeter; storage?: boolean; note?: string }) {
  const amount = (n: number) => (storage ? gigabytes(n) : n.toLocaleString());
  const unit = storage ? ' GB' : '';
  const text = meter.allowance === null
    ? `${amount(meter.used)}${unit}, unlimited`
    : `${amount(meter.used)} of ${amount(meter.allowance)}${unit}`;
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <span style={{ fontSize: 12, whiteSpace: 'nowrap' }} title={note}>
        {text}
        {note && <span className={styles.muted}>*</span>}
      </span>
      {meter.share !== null && <UtilizationBar ratio={meter.share} width={64} />}
    </div>
  );
}

/** What the add-ons put into a meter's allowance, for its tooltip. */
function addonNote(c: AllowanceCompany, key: AllowanceMeterKey): string | undefined {
  const minutes = (boostMinutes: number, packSeconds: number) => {
    const parts: string[] = [];
    if (boostMinutes > 0) parts.push(`${boostMinutes.toLocaleString()} boost minutes`);
    if (packSeconds >= 60) parts.push(`${Math.floor(packSeconds / 60).toLocaleString()} pack minutes`);
    return parts.length === 0 ? undefined : `Includes ${parts.join(' and ')}`;
  };
  switch (key) {
    case 'recordingMinutes': return minutes(c.addons.recordingBoostMinutes, c.addons.recordingPackSeconds);
    case 'narrationMinutes': return minutes(c.addons.narrationBoostMinutes, c.addons.narrationPackSeconds);
    case 'storageBytes': return c.addons.videoHistoryActive && c.meters.storageBytes.allowance !== null ? 'Includes video history, eight times the plan\'s storage' : undefined;
    default: return undefined;
  }
}

function gigabytes(bytes: number): string {
  const gb = bytes / GB;
  if (Number.isInteger(gb)) return gb.toLocaleString();
  return gb < 0.1 ? '<0.1' : gb.toFixed(1);
}

/** "2026-10" as "October 2026". */
function monthName(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  if (!year || !mon) return month;
  return new Date(Date.UTC(year, mon - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }); // a day, not a moment (TOADO-904)
}

/** Amber from 80%, red at 100%: the ticket cap's bar and every allowance meter. */
function utilizationState(ratio: number): 'ok' | 'warn' | 'full' {
  return ratio >= 1 ? 'full' : ratio >= 0.8 ? 'warn' : 'ok';
}

const UTILIZATION_COLORS = { ok: '#16a34a', warn: '#d97706', full: '#dc2626' } as const;

function UtilizationBar({ ratio, width = 100 }: { ratio: number; width?: number }) {
  const pct = Math.min(100, Math.round(ratio * 100));
  const state = utilizationState(ratio);
  return (
    <div data-state={state} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width, height: 6, background: 'var(--bg-muted)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: UTILIZATION_COLORS[state] }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

